/**
 * Pure planning helpers for derivatives (no DOM): filmstrip layout, proxy sizing and the
 * thumbnail time. Kept separate so they are unit-testable and shared by all generators.
 */
import type { FilmstripInfo } from '@ve/core';

export const FILMSTRIP_TILE_HEIGHT = 90;
export const FILMSTRIP_MAX_TILES = 120;
/** Shortest interval between filmstrip tiles, seconds. */
export const FILMSTRIP_MIN_INTERVAL = 0.5;

/** Same semantics as the former server filmstrip: tile `i` covers `[i*interval, (i+1)*interval)`. */
export function planFilmstrip(duration: number, width: number, height: number): FilmstripInfo {
  const safeDuration = Math.max(0.001, duration);
  const interval = Math.max(FILMSTRIP_MIN_INTERVAL, safeDuration / FILMSTRIP_MAX_TILES);
  const count = Math.max(1, Math.min(FILMSTRIP_MAX_TILES, Math.ceil(safeDuration / interval)));
  const tileHeight = FILMSTRIP_TILE_HEIGHT;
  const aspect = width > 0 && height > 0 ? width / height : 16 / 9;
  const tileWidth = Math.max(2, Math.round((tileHeight * aspect) / 2) * 2);
  return { count, interval, tileWidth, tileHeight };
}

/** Timestamps (seconds) to sample for each filmstrip tile: the middle of its interval. */
export function filmstripTimestamps(info: FilmstripInfo, duration: number): number[] {
  const last = Math.max(0, duration - 0.001);
  return Array.from({ length: info.count }, (_, i) => Math.min(last, (i + 0.5) * info.interval));
}

/** Proxies are only worth making for sources taller than this. */
export const PROXY_MAX_HEIGHT = 720;
/** Short GOP so seeking anywhere decodes few frames. */
export const PROXY_KEYFRAME_INTERVAL = 0.4;

export function needsProxy(height: number | undefined): boolean {
  return height !== undefined && height > PROXY_MAX_HEIGHT;
}

/** Proxy frame size: at most 720 px tall, aspect preserved, even dimensions (H.264 requirement). */
export function proxySize(width: number, height: number): { width: number; height: number } {
  const scale = Math.min(1, PROXY_MAX_HEIGHT / height);
  const even = (v: number) => Math.max(2, Math.round((v * scale) / 2) * 2);
  return { width: even(width), height: even(height) };
}

/** Moderate bitrate for preview quality: ~0.08 bits per pixel per frame. */
export function proxyBitrate(width: number, height: number, fps = 30): number {
  return Math.round(Math.min(6_000_000, Math.max(500_000, width * height * Math.min(fps, 60) * 0.08)));
}

/** Where to grab a video's thumbnail: 10 % in, but at most 1 s (skips black intros, stays cheap). */
export function thumbnailTime(duration: number | undefined): number {
  if (!duration || duration <= 0) return 0;
  return Math.min(1, duration * 0.1, Math.max(0, duration - 0.001));
}

export const THUMBNAIL_WIDTH = 320;

/** Scales a size to a target width, keeping aspect and never upscaling. */
export function fitWidth(width: number, height: number, target: number): { width: number; height: number } {
  const scale = Math.min(1, target / width);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}
